/**
 * Company Store — replaces `$rootScope.G_CompanyDetails`.
 *
 * Holds the active vendor company context for the Vendor Management module.
 * Shape mirrors the AngularJS `companyDetailsService` payload:
 *
 *   $rootScope.G_CompanyDetails = { company: { id, name, ... } }
 *
 * Reads come from many vendor routes; writes come primarily from
 * `ServiceAreaUpdateRoute` (sets the active company when a service area is
 * selected). Embedded mode dual-writes back to `$rootScope` so the AngularJS
 * sidebar / company menu stays in sync.
 */

import { create } from 'zustand';
import { setRootScopeProperty, getRootScopeProperty } from '@/lib/legacyContext';
import { getMountConfig } from '@/mountConfig';

export interface CompanyDetails {
  /**
   * The active company. Shape varies across the Vendor Management module —
   * different routes pass partial or fuller payloads. Kept loose so callers
   * can pass their narrower local type without unsafe casts.
   */
  company?: {
    id?: string | number | null;
    name?: string | null;
    vendorCode?: string | null;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

interface CompanyStoreState {
  details: CompanyDetails | null;
}

interface CompanyStoreActions {
  setDetails: (details: CompanyDetails | null) => void;
  clear: () => void;
  hydrateFromLegacy: () => void;
}

type CompanyStore = CompanyStoreState & CompanyStoreActions;

function syncToLegacy(value: CompanyDetails | null): void {
  try {
    if (getMountConfig().mode === 'embedded') {
      setRootScopeProperty('G_CompanyDetails', value);
    }
  } catch {
    // Graceful — AngularJS may not be present.
  }
}

export const useCompanyStore = create<CompanyStore>()((set) => ({
  details: null,

  setDetails: (details) => {
    set({ details });
    syncToLegacy(details);
  },

  clear: () => {
    set({ details: null });
    syncToLegacy(null);
  },

  hydrateFromLegacy: () => {
    // Embedded-only — see propertyStore.hydrateFromLegacy for full reasoning.
    if (getMountConfig().mode !== 'embedded') return;
    const value = getRootScopeProperty<CompanyDetails>('G_CompanyDetails');
    set({ details: value && Object.keys(value).length > 0 ? value : null });
  },
}));

export const useCompanyDetails = (): CompanyDetails | null => useCompanyStore((s) => s.details);
export const useCompanyId = (): string | number | null | undefined =>
  useCompanyStore((s) => s.details?.company?.id);

/** Test-only helper. */
export function __resetCompanyStoreForTests(): void {
  useCompanyStore.setState({ details: null });
}
